import idx from 'idx'
import axios from 'axios'
import {API_URL, SAUCE_API_URL} from 'config'
import {push} from 'connected-react-router'

import {normalize} from 'normalizr'
import initialState from 'reducers/initialState'
import {groupSchema, groupReportSchema} from 'modules/schema'

import {LOCATION_CHANGE} from 'constants/actions'
import errorDispatcher from 'utils/errorDispatcher'
import {getQueryParams, getPaginationData, paginationFromCount, extractErrorCode} from 'utils/api'
import {getAuthorizationHeader} from 'utils/session'

// Actions
export const SELECT_GROUP = 'SELECT_GROUP'
export const API_GET_GROUPS_REQUEST = 'API_GET_GROUPS_REQUEST'
export const API_GET_GROUPS_SUCCESS = 'API_GET_GROUPS_SUCCESS'
export const API_GET_GROUPS_ERROR = 'API_GET_GROUPS_ERROR'
export const API_GET_USER_GROUPS_REQUEST = 'API_GET_USER_GROUPS_REQUEST'
export const API_GET_USER_GROUPS_SUCCESS = 'API_GET_USER_GROUPS_SUCCESS'
export const API_GET_USER_GROUPS_ERROR = 'API_GET_USER_GROUPS_ERROR'
export const API_GET_ALL_USER_GROUPS_REQUEST = 'API_GET_ALL_USER_GROUPS_REQUEST'
export const API_GET_ALL_USER_GROUPS_SUCCESS = 'API_GET_ALL_USER_GROUPS_SUCCESS'
export const API_GET_ALL_USER_GROUPS_ERROR = 'API_GET_ALL_USER_GROUPS_ERROR'
export const API_CREATE_GROUP_REQUEST = 'API_CREATE_GROUP_REQUEST'
export const API_CREATE_GROUP_SUCCESS = 'API_CREATE_GROUP_SUCCESS'
export const API_CREATE_GROUP_ERROR = 'API_CREATE_GROUP_ERROR'
export const API_UPDATE_GROUP_REQUEST = 'API_UPDATE_GROUP_REQUEST'
export const API_UPDATE_GROUP_SUCCESS = 'API_UPDATE_GROUP_SUCCESS'
export const API_UPDATE_GROUP_ERROR = 'API_UPDATE_GROUP_ERROR'
export const API_DELETE_GROUP_REQUEST = 'API_DELETE_GROUP_REQUEST'
export const API_DELETE_GROUP_SUCCESS = 'API_DELETE_GROUP_SUCCESS'
export const API_DELETE_GROUP_ERROR = 'API_DELETE_GROUP_ERROR'
export const API_GENERATE_GROUP_REPORT_REQUEST = 'API_GENERATE_GROUP_REPORT_REQUEST'
export const API_GENERATE_GROUP_REPORT_SUCCESS = 'API_GENERATE_GROUP_REPORT_SUCCESS'
export const API_GENERATE_GROUP_REPORT_ERROR = 'API_GENERATE_GROUP_REPORT_ERROR'

// Reducer
export default function reducer(state = initialState.groups, action) {
    switch (action.type) {
        case LOCATION_CHANGE:
            return {
                ...state,
                items: null,
                userItems: null,
                userItemsAll: null,
            }
        case SELECT_GROUP:
        case API_UPDATE_GROUP_SUCCESS:
            return {
                ...state,
                selected: action.group
            }
        case API_GET_GROUPS_REQUEST:
            return {
                ...state,
                loading: true,
            }
        case API_GET_USER_GROUPS_REQUEST:
            return {
                ...state,
                loading: true,
            }
        case API_GET_GROUPS_SUCCESS:
            return {
                ...state,
                loading: false,
                items: action.groups
            }
        case API_GET_USER_GROUPS_SUCCESS:
            return {
                ...state,
                loading: false,
                userItems: action.groups
            }
        case API_GET_ALL_USER_GROUPS_SUCCESS:
            return {
                ...state,
                userItemsAll: action.groups
            }
        default:
            return state
    }
}

// Action Creators
export function getGroups(organisation, filters, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_GET_GROUPS_REQUEST})
        try {
            let response = await axios.get(`${API_URL}/project/${organisation.uuid}/group${getQueryParams('?', filters)}`, getAuthorizationHeader())
            let result = response.data.data
            if (!result) throw Error("no response data!")

            // They use the same endpoint but different field names 🙄
            result.forEach(_ => {
                _.uuid = _.groupUuid
                _.name = _.groupName
            })
            const pagination = paginationFromCount(response.data.total)

            if (response) {
                const {entities: {groups}} = normalize(result, [groupSchema])
                dispatch({type: API_GET_GROUPS_SUCCESS, groups: groups || {}})

                if (callback) {
                    dispatch(() => callback(pagination))
                }
            }
            else {
                throw Error
            }
        }
        catch (error) {
            console.error("getGroups", error)
            const errorCode = idx(error, _ => _.response.status) || ''
            errorDispatcher(errorCode, API_GET_GROUPS_ERROR, dispatch)
        }
    }
}

export function getUserGroups(user, organisation, filters, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_GET_USER_GROUPS_REQUEST})
        try {
            const response = await axios.get(
                `${API_URL}/project/${organisation.uuid}/user/${user.id}/groups${getQueryParams('?', filters)}`,
                getAuthorizationHeader()
            )
            const pagination = paginationFromCount(response.data.total)

            const {entities: {groups}} = normalize(response.data.data, [groupReportSchema])
            dispatch({type: API_GET_USER_GROUPS_SUCCESS, groups: groups || {}})

            if (callback) {
                dispatch(() => callback(pagination))
            }
        }
        catch (error) {
            const errorCode = idx(error, _ => _.data.status) || ''
            errorDispatcher(errorCode, API_GET_USER_GROUPS_ERROR, dispatch)
        }
    }
}

export function getAllUserGroups(user, organisation) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_GET_ALL_USER_GROUPS_REQUEST})
        try {
            const response = await axios.get(
                `${SAUCE_API_URL}/project/${organisation.uuid}/user/${user.id}/groups?limit=10000`,
                getAuthorizationHeader()
            )
            const {entities: {groups}} = normalize(response.data.data, [groupReportSchema])
            dispatch({type: API_GET_ALL_USER_GROUPS_SUCCESS, groups: groups || {}})
        }
        catch (error) {
            const errorCode = idx(error, _ => _.data.status) || ''
            errorDispatcher(errorCode, API_GET_ALL_USER_GROUPS_ERROR, dispatch)
        }
    }
}

export function createGroup(data, organisation, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_CREATE_GROUP_REQUEST})
        try {
            const group = (await axios.post(`${SAUCE_API_URL}/project/${organisation.uuid}/group/create`, data, getAuthorizationHeader())).data
            dispatch({type: API_CREATE_GROUP_SUCCESS, group})

            if (callback) {
                dispatch(() => callback(group))
            }
        }
        catch (error) {
            console.error("createGroup", error)
            const errorCode = idx(error, _ => _.data.status) || ''
            errorDispatcher(errorCode, API_CREATE_GROUP_ERROR, dispatch)
        }

    }
}

export function updateGroup(organisation, selectedGroup, values) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_UPDATE_GROUP_REQUEST})

        try {
            await axios.put(`${API_URL}/group/${selectedGroup.uuid}`, {name: values.name}, getAuthorizationHeader())
            // old api returned a copy, but let's not
            const group = {...selectedGroup}
            group.name = values.name
            dispatch({type: API_UPDATE_GROUP_SUCCESS, group})
        }
        catch (error) {
            console.log("updateGroup", error)
            const errorCode = extractErrorCode(error)
            errorDispatcher(errorCode, API_UPDATE_GROUP_REQUEST, dispatch)
        }
    }
}

export function deleteGroup(organisation, group, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_DELETE_GROUP_REQUEST})
        try {
            await axios.delete(`${API_URL}/group/${group.uuid}`, getAuthorizationHeader())
            dispatch({type: API_DELETE_GROUP_SUCCESS})
            dispatch(push('/dashboard/manage/groups'))

            if (callback) {
                dispatch(callback)
            }
        }
        catch (error) {
            console.log("deleteGroup", error)
            const errorCode = extractErrorCode(error)
            errorDispatcher(errorCode, API_DELETE_GROUP_ERROR, dispatch)
        }
    }
}

export function getReportGroups(organisation, filters, callback) {
    return async function dispatcher(dispatch) {
        dispatch({type: API_GET_GROUPS_REQUEST})
        try {
            const response = await axios.get(`${API_URL}/project/${organisation.uuid}/group${getQueryParams('?', filters)}`, getAuthorizationHeader())
            const result = response.data.data
            if (!result) throw Error("no response data!")
            // They use the same endpoint but different field names 🙄
            result.forEach(_ => {
                _.uuid = _.groupUuid
                _.name = _.groupName
            })

            const pagination = paginationFromCount(response.data.total)

            const {entities: {groups}} = normalize(result, [groupReportSchema])
            dispatch({type: API_GET_GROUPS_SUCCESS, groups: groups || {}})

            if (callback) {
                dispatch(() => callback(pagination))
            }
        }
        catch (error) {
            console.error("getReportGroups", error)
            const errorCode = idx(error, _ => _.response.status) || ''
            errorDispatcher(errorCode, API_GET_GROUPS_ERROR, dispatch)
        }
    }
}

export function showGroupDetail(group) {
    return async function dispatcher(dispatch) {
        dispatch({type: SELECT_GROUP, group})
        dispatch(push('/dashboard/data/groups/details'))
    }
}

export function selectManageGroup(group) {
    return async function dispatcher(dispatch) {
        dispatch({type: SELECT_GROUP, group})
        dispatch(push('/dashboard/manage/groups/traps'))
    }
}



// WEBPACK FOOTER //
// ./src/modules/group.js